/**
 * AlloySellPanel.js
 *
 * Phase 2 — sell refined alloys for credits. Reused by both the orbital
 * Station trade terminal (no fee) and the placeable Trade Terminal structure
 * (applies its fee, enforced server-side). Backed by /api/crafting/sell-alloy.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Coins, Boxes } from 'lucide-react';

const TIER_TONE = {
  1: 'border-slate-400/30 text-slate-200',
  2: 'border-cyan-400/40 text-cyan-200',
  3: 'border-fuchsia-400/40 text-fuchsia-200',
  4: 'border-amber-400/40 text-amber-200',
};

export default function AlloySellPanel({ api, terminal = 'station', structureId, accent = 'emerald', onSold }) {
  const [alloys, setAlloys] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/api/crafting/state');
      setAlloys(data?.alloys || []);
    } catch (_e) { /* noop */ }
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  const sell = useCallback(async (alloyId) => {
    setBusy(true);
    try {
      const { data } = await api.post('/api/crafting/sell-alloy', {
        alloy_id: alloyId, terminal, structure_id: structureId,
      });
      setAlloys(data?.alloys || []);
      const paid = Math.round(data?.total_paid || 0);
      toast.success(`Sold alloys · +${paid.toLocaleString()} cr`);
      onSold?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sell failed');
    } finally { setBusy(false); }
  }, [api, terminal, structureId, onSold]);

  const totalValue = alloys.reduce((a, s) => a + (s.qty || 0) * (s.value_per_unit || 0), 0);
  const borderCls = accent === 'amber' ? 'border-amber-400/25' : 'border-emerald-400/25';

  return (
    <div className={`rounded-lg border ${borderCls} bg-emerald-400/[0.04] p-3`} data-testid="alloy-sell-panel">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-[0.18em] text-emerald-300/90 flex items-center gap-1.5">
          <Boxes className="w-3.5 h-3.5" /> Refined Alloys
        </span>
        {terminal === 'structure' && (
          <span className="text-[10px] text-slate-500">terminal fee applies</span>
        )}
      </div>

      {alloys.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-3 text-center">
          No alloys to sell. Fabricate some at a Refinery or ship Fabricator.
        </div>
      ) : (
        <>
          <div className="space-y-1.5 mb-3">
            {alloys.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-3 py-1.5 rounded border border-emerald-400/20 bg-black/30" data-testid={`alloy-sell-row-${a.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Badge variant="outline" className={`text-[10px] ${TIER_TONE[a.tier] || ''}`}>T{a.tier}</Badge>
                  <span className="text-sm text-slate-200 truncate">{a.name}</span>
                  <span className="text-xs text-slate-500 tabular-nums">×{Math.floor(a.qty)}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] text-amber-200/80 tabular-nums inline-flex items-center gap-0.5">
                    <Coins className="w-3 h-3" />{a.value_per_unit}
                  </span>
                  <Button
                    size="sm" disabled={busy} onClick={() => sell(a.id)}
                    data-testid={`alloy-sell-${a.id}`}
                    className="h-6 px-2 text-[11px] bg-emerald-500/90 hover:bg-emerald-400 text-slate-900 font-semibold"
                  >
                    Sell
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">Total ≈ <span className="font-mono text-emerald-300 tabular-nums">{Math.round(totalValue).toLocaleString()} cr</span></span>
            <Button
              size="sm" disabled={busy} onClick={() => sell(null)}
              data-testid="alloy-sell-all"
              className="h-7 text-[10px] tracking-[0.2em] uppercase font-semibold bg-emerald-500/90 hover:bg-emerald-400 text-slate-900"
            >
              Sell All
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
